import { useContext, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Logout from '@mui/icons-material/Logout';
import Grid from '@mui/material/Grid';
import Drawer from '@mui/material/Drawer';
import MenuIcon from '@mui/icons-material/Menu';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useLocation, useNavigate } from 'react-router-dom';
import { Context } from '../../../context/Context';
import { hasScope, isStaff } from '../../../helpers/scope/Scope';
import '../../../styles/Menu.css';
import constants from '../../../constants/Constants';

export default function AccountMenu() {
  const navigate = useNavigate();
  const { state, handleLogout } = useContext(Context);
  const lang = state.lang;
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width:768px)');

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const toggleDrawer = (open) => (event) => setDrawerOpen(open);
  const handleMenuKeyDown = (callback) => (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      callback();
    }
  };

  const activeZone = useMemo(() => {
    const normalizedPath = location.pathname.replace(/^\/+/, '');
    const zone = normalizedPath ? normalizedPath.split('/')[0] : '';
    return {
      users: zone === 'users' ? 'active' : '',
      vendors: zone === 'vendors' ? 'active' : '',
      stock: zone === 'stock' ? 'active' : '',
      reports: zone === 'reports' ? 'active' : '',
      purchases: zone === 'purchases' ? 'active' : '',
    };
  }, [location.pathname]);

  const getUserInitial = () => {
    const username = state?.user?.username;
    if (!username || typeof username !== 'string') return '?';
    const trimmed = username.trim();
    return trimmed ? trimmed[0].toUpperCase() : '?';
  };

  const renderMenuItems = () => (
    <>
      {hasScope(constants.SCOPE_USER_READ, state.user) && (
        <Typography
          onClick={() => navigate('/users')}
          onKeyDown={handleMenuKeyDown(() => navigate('/users'))}
          role='button'
          tabIndex={0}
          className={`item-menu ${activeZone.users}`}
        >
          {lang === 'ES' ? 'Usuarios' : 'Users'}
        </Typography>
      )}
      {hasScope(constants.SCOPE_STOCK_SETTINGS_READ, state.user) && isStaff(state.user) && (
        <Typography
          onClick={() => navigate('/vendors')}
          onKeyDown={handleMenuKeyDown(() => navigate('/vendors'))}
          role='button'
          tabIndex={0}
          className={`item-menu ${activeZone.vendors}`}
        >
          {lang === 'ES' ? 'Proveedores' : 'Vendors'}
        </Typography>
      )}
      {hasScope(constants.SCOPE_STOCK_READ, state.user) && (
        <Typography
          onClick={() => navigate('/stock')}
          onKeyDown={handleMenuKeyDown(() => navigate('/stock'))}
          role='button'
          tabIndex={0}
          className={`item-menu ${activeZone.stock}`}
        >
          {lang === 'ES' ? 'Inventario' : 'Stock'}
        </Typography>
      )}
      {hasScope(constants.SCOPE_REPORTS_READ, state.user) && (
        <Typography
          onClick={() => navigate('/reports')}
          onKeyDown={handleMenuKeyDown(() => navigate('/reports'))}
          role='button'
          tabIndex={0}
          className={`item-menu ${activeZone.reports}`}
        >
          {lang === 'ES' ? 'Reportes' : 'Reports'}
        </Typography>
      )}

      {hasScope(constants.SCOPE_PURCHASE_ORDERS_READ, state.user) && (
        <Typography
          onClick={() => navigate('/purchases')}
          onKeyDown={handleMenuKeyDown(() => navigate('/purchases'))}
          role='button'
          tabIndex={0}
          className={`item-menu ${activeZone.purchases}`}
        >
          {lang === 'ES' ? 'Órdenes de Compra' : 'Purchase Orders'}
        </Typography>
      )}
    </>
  );

  return (
    <>
      <Grid container direction='row' justifyContent='space-between' alignItems='center' className='menu'>
        <img src={require('../../../assets/images/logo.png')} alt='logo' className='logo-menu' />
        {isMobile ? (
          <>
            <IconButton
              onClick={toggleDrawer(true)}
              edge='start'
              color='inherit'
              aria-label={lang === 'ES' ? 'Abrir menú' : 'Open menu'}
              title={lang === 'ES' ? 'Abrir menú' : 'Open menu'}
            >
              <MenuIcon style={{ color: 'white' }} />
            </IconButton>
            <Drawer anchor='left' open={drawerOpen} onClose={toggleDrawer(false)}>
              <Box p={2} role='presentation' onClick={toggleDrawer(false)} onKeyDown={toggleDrawer(false)}>
                {renderMenuItems()}
              </Box>
            </Drawer>
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
            {renderMenuItems()}
            <Tooltip title={lang === 'ES' ? 'Configuración de cuenta' : 'Account settings'}>
              <IconButton
                onClick={handleClick}
                size='small'
                sx={{ ml: 2 }}
                aria-label={lang === 'ES' ? 'Abrir menú de cuenta' : 'Open account menu'}
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'white', color: constants.colors.primary }}>
                  {getUserInitial()}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Grid>
      <Menu
        anchorEl={anchorEl}
        id='account-menu'
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          sx: { mt: 1.5, overflow: 'visible', filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))' },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem>
          <Avatar /> {state?.user?.username || ''}
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            handleLogout();
            navigate(state.lang === 'ES' ? '/login' : `/login/${state.lang}`);
          }}
        >
          <ListItemIcon>
            <Logout fontSize='small' />
          </ListItemIcon>
          {lang === 'ES' ? 'Cerrar sesión' : 'Logout'}
        </MenuItem>
      </Menu>
    </>
  );
}
